import React, { useState, useCallback, useMemo } from 'react'
import styled from 'styled-components'
import CruiseAccordion from './CruiseAccordion'
import Heading from 'components/Luxkit/Typography/Heading'
import BodyText from 'components/Luxkit/Typography/BodyText'
import SquareCheckboxInput from 'components/Luxkit/Checkbox/SquareCheckboxInput'
import Group from 'components/utils/Group'
import { arrayToObject } from 'lib/arrayUtils'
import ItinerarySideLine from './ItinerarySideLine'

const TitleBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const ItineraryAccordionArea = styled.div`
  flex: 1 1 auto;
`

interface Props {
  itinerary: Array<App.CruiseItineraryItem>
  title?: string
  portNameSelected?: string | null
  daySelected?: number | null
  onSelectPort?: (port?: App.CruisePort, startDay?: number) => void
}

type UniqueItineraryItem = Record<string, {
  id: string
  item: App.CruiseItineraryItem,
  addPortCircle: boolean
  portName: string
  portNumber: number
  isStart: boolean
  isEnd: boolean
}>

function CruiseItinerary(props: Props) {
  const { itinerary, title, onSelectPort, portNameSelected, daySelected } = props
  const [expandedAccordions, setExpandedAccordions] = useState<Record<string, boolean>>(arrayToObject(itinerary, item => `${item.startDay}`, () => false))
  const [showOnlyPorts, setShowOnlyPorts] = useState<boolean>(false)

  // Remove duplicate days at sea
  const uniqueItineraryList = useMemo(() => {
    const itineraryWithoutRepeatedPorts: App.CruiseItineraryItem[] = Object.values(itinerary.reduce((acc, item) => ({
      ...acc,
      [`${item.startDay}_${item.port?.name || 'at_sea'}`]: item,
    }), {}))

    let portNumber = 0
    let lastPortName = ''

    return Object.values(itineraryWithoutRepeatedPorts.reduce((acc, item, index) => {
      if (showOnlyPorts && !item.port) return acc

      const portName = item.port?.name || 'at_sea'
      const key = `${item.startDay}_${portName}`
      const isStart = index === 0
      const isEnd = index === itineraryWithoutRepeatedPorts.length - 1

      // add port circle if port is different from previous item
      const addPortCircle = !!item.port && lastPortName !== item.port?.name

      const result = {
        ...acc,
        [key]: {
          id: key,
          portName,
          item,
          portNumber,
          addPortCircle,
          isStart,
          isEnd,
        },
      }

      if (addPortCircle) {
        portNumber += 1
        lastPortName = item.port?.name || ''
      }

      return result
    }, {} as UniqueItineraryItem))
  }, [itinerary, showOnlyPorts])

  const toggleAccordion = useCallback((key: string) => {
    const newExpandedValue = !expandedAccordions[key]
    setExpandedAccordions({ ...expandedAccordions, [key]: newExpandedValue })

    if (onSelectPort) {
      const itineraryItem = itinerary.find(item => `${item.startDay}` === key)
      onSelectPort(itineraryItem?.port || undefined, itineraryItem?.startDay)
    }
  }, [expandedAccordions, itinerary, onSelectPort])

  const toggleAllAccordion = useCallback(() => {
    const hasClosedItems = Object.values(expandedAccordions).some(item => !item)

    if (hasClosedItems) {
      setExpandedAccordions(arrayToObject(itinerary, item => `${item.startDay}`, () => true))
    } else {
      setExpandedAccordions(arrayToObject(itinerary, item => `${item.startDay}`, () => false))
    }
  }, [expandedAccordions, itinerary])

  return <Group direction="vertical" gap={20}>
    <Group direction="vertical" gap={4}>
      {!!title && <TitleBar>
        <Heading variant="heading3" align="center">{title}</Heading>
      </TitleBar>}

      <BodyText variant="medium">
        Please note that all dates and times are displayed in local time.
      </BodyText>
    </Group>

    <Group direction="horizontal" gap={20}>
      <SquareCheckboxInput
        variant="large"
        onChange={() => setShowOnlyPorts(!showOnlyPorts)}
        checked={showOnlyPorts}
      >
        Show ports only
      </SquareCheckboxInput>

      <SquareCheckboxInput
        variant="large"
        onChange={toggleAllAccordion}
        checked={Object.values(expandedAccordions).every(item => item)}
      >
        Expand all
      </SquareCheckboxInput>
    </Group>

    <div>
      {uniqueItineraryList.map(({ id, item, portNumber, portName, isStart, isEnd, addPortCircle }) => {
        const isSelected = portNameSelected === item.port?.name && (!daySelected || daySelected === item.startDay)

        return <Group
          key={id}
          direction="horizontal"
          gap={16}
        >
          <ItinerarySideLine
            portName={portName}
            portNumber={portNumber}
            isStart={isStart}
            isEnd={isEnd}
            addPortCircle={addPortCircle}
          />
          <ItineraryAccordionArea>
            <CruiseAccordion
              itineraryItem={item}
              isSelected={isSelected}
              open={expandedAccordions[item.startDay] && !!item.description}
              toggleAccordion={toggleAccordion}
            />
          </ItineraryAccordionArea>
        </Group>
      })}
    </div>
  </Group>
}

export default CruiseItinerary
